import React, { useEffect, useState } from 'react';
import api from '../api';
import DrillDownModal from '../components/DrillDownModal';
import BSFIProfileAnalysis from '../components/BSFIProfileAnalysis';
import { Eye, Warning, ShieldCheck, Bank, MapPin } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ScatterChart, Scatter, ZAxis } from 'recharts';

const RISK_COLORS = { 'Very High': '#8B1A1A', 'High': '#C6493A', 'Medium': '#D9943B', 'Low': '#2D7D46' };
const MATURITY_COLORS = { 'Advanced': '#2D7D46', 'Intermediate': '#D9943B', 'Basic': '#C6493A' };

export default function SupervisoryIntel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillKey, setDrillKey] = useState(null);
  const [selectedBsfi, setSelectedBsfi] = useState(null);

  useEffect(() => {
    api.get('/api/dashboard/supervisory').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="animate-pulse text-content-secondary">Loading supervisory data...</div>;
  if (!data) return <div className="text-status-alert">Failed to load</div>;

  if (selectedBsfi) {
    return <BSFIProfileAnalysis bsfiId={selectedBsfi} onBack={() => setSelectedBsfi(null)} />;
  }

  const { bsfis, hazards } = data;
  const highPhysical = bsfis.filter(b => b.physical_risk_score >= 75).length;
  const highTransition = bsfis.filter(b => b.transition_risk_score >= 60).length;
  const openRemediation = bsfis.filter(b => b.remediation_status === 'Open').length;
  const totalExposure = hazards.reduce((a, h) => a + (h.exposure_value_bn || 0), 0);

  // Risk scatter data
  const scatterData = bsfis.map(b => ({ x: b.physical_risk_score, y: b.transition_risk_score, z: Math.abs(b.stress_car_impact_bps), name: b.bsfi_id }));

  // Hazard by region
  const regionExposure = {};
  hazards.forEach(h => { regionExposure[h.region] = (regionExposure[h.region] || 0) + (h.exposure_value_bn || 0); });
  const exposureChart = Object.entries(regionExposure).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);

  return (
    <div data-testid="supervisory-intel" className="space-y-5 animate-fade-in">
      <div>
        <h1 className="font-heading font-light text-3xl text-content-primary tracking-tight">Supervisory Intelligence Layer</h1>
        <p className="text-sm text-content-secondary mt-1">Climate, transition, physical, and nature-risk intelligence across BSP-supervised entities</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { icon: Warning, label: 'High Physical Risk', val: highPhysical, sub: `of ${bsfis.length} BSFIs`, color: 'text-status-alert', dk: 'sup_high_physical' },
          { icon: Eye, label: 'High Transition Risk', val: highTransition, sub: `of ${bsfis.length} BSFIs`, color: 'text-status-watch', dk: 'sup_high_transition' },
          { icon: ShieldCheck, label: 'Open Remediation', val: openRemediation, sub: 'requiring action', color: openRemediation > 0 ? 'text-status-alert' : 'text-status-on-track', dk: 'sup_open_remediation' },
          { icon: MapPin, label: 'Total Exposure', val: `₱${totalExposure.toFixed(1)}B`, sub: 'hazard zones', color: 'text-brand-primary', dk: 'sup_total_exposure' },
        ].map((k, i) => (
          <div key={i} onClick={() => setDrillKey(k.dk)} className="bg-white border border-border-default rounded-lg p-3.5 cursor-pointer hover:-translate-y-0.5 hover:shadow-sm transition-all group">
            <k.icon size={16} className={`${k.color} mb-1.5`} />
            <p className={`text-xl font-heading font-bold ${k.color}`}>{k.val}</p>
            <p className="text-[10px] text-content-secondary font-medium">{k.label}</p>
            <p className="text-[9px] text-content-disabled">{k.sub}</p>
            <p className="text-[8px] text-brand-accent opacity-0 group-hover:opacity-100 transition-opacity mt-0.5">Click to drill down</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Risk Scatter */}
        <div className="bg-white border border-border-default rounded-lg p-4">
          <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">BSFI Risk Profile (Physical vs Transition)</h3>
          <ResponsiveContainer width="100%" height={240}>
            <ScatterChart>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F3F5" />
              <XAxis dataKey="x" name="Physical Risk" tick={{ fontSize: 10 }} label={{ value: 'Physical Risk', position: 'bottom', fontSize: 10 }} />
              <YAxis dataKey="y" name="Transition Risk" tick={{ fontSize: 10 }} label={{ value: 'Transition', angle: -90, position: 'insideLeft', fontSize: 10 }} />
              <ZAxis dataKey="z" range={[60, 300]} />
              <Tooltip formatter={(v, name) => [v, name === 'x' ? 'Physical' : name === 'y' ? 'Transition' : 'CAR Impact (bps)']} />
              <Scatter data={scatterData} fill="#C2714F" />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
        {/* Regional Exposure */}
        <div className="bg-white border border-border-default rounded-lg p-4">
          <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">Hazard Exposure by Region (₱Bn)</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={exposureChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F3F5" />
              <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#49564C' }} />
              <YAxis tick={{ fontSize: 10, fill: '#49564C' }} />
              <Tooltip formatter={v => [`₱${v.toFixed(1)}B`]} />
              <Bar dataKey="value" radius={[3, 3, 0, 0]} fill="#C2714F" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* BSFI Table */}
      <div className="bg-white border border-border-default rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-border-default flex items-center justify-between">
          <h3 className="font-heading font-semibold text-sm text-content-primary">BSFI Climate Risk Profiles</h3>
          <span className="text-[10px] text-content-disabled">Click any row for advanced analysis</span>
        </div>
        <div className="overflow-x-auto">
          <table data-testid="bsfi-table" className="w-full text-[12px]">
            <thead><tr className="bg-surface-muted">
              {['BSFI', 'Type', 'Region', 'Exposure', 'Physical', 'Transition', 'Nature', 'Stress CAR', 'ESRM', 'Remediation'].map(h => (
                <th key={h} className="text-left px-4 py-2.5 font-semibold text-content-secondary text-[10px] uppercase tracking-wider">{h}</th>
              ))}
            </tr></thead>
            <tbody className="divide-y divide-border-subtle">
              {bsfis.map((b, i) => (
                <tr key={i} data-testid={`bsfi-row-${b.bsfi_id}`} onClick={() => setSelectedBsfi(b.bsfi_id)}
                  className="hover:bg-brand-primary/5 cursor-pointer transition-colors group">
                  <td className="px-4 py-2.5 font-mono text-[10px] font-medium text-brand-primary group-hover:underline">{b.bsfi_id}</td>
                  <td className="px-4 py-2.5">{b.bank_type}</td>
                  <td className="px-4 py-2.5">{b.region}</td>
                  <td className="px-4 py-2.5 text-[10px] max-w-[120px]">{b.primary_exposure}</td>
                  <td className="px-4 py-2.5"><span className={`font-mono font-semibold ${b.physical_risk_score >= 75 ? 'text-status-alert' : b.physical_risk_score >= 50 ? 'text-status-watch' : 'text-status-on-track'}`}>{b.physical_risk_score}</span></td>
                  <td className="px-4 py-2.5"><span className={`font-mono font-semibold ${b.transition_risk_score >= 60 ? 'text-status-alert' : b.transition_risk_score >= 40 ? 'text-status-watch' : 'text-status-on-track'}`}>{b.transition_risk_score}</span></td>
                  <td className="px-4 py-2.5"><span className={`font-mono font-semibold ${b.nature_risk_score >= 50 ? 'text-status-watch' : 'text-status-on-track'}`}>{b.nature_risk_score}</span></td>
                  <td className="px-4 py-2.5 font-mono text-status-alert font-semibold">{b.stress_car_impact_bps} bps</td>
                  <td className="px-4 py-2.5"><span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ backgroundColor: `${MATURITY_COLORS[b.esrm_maturity] || '#87A98E'}15`, color: MATURITY_COLORS[b.esrm_maturity] || '#87A98E' }}>{b.esrm_maturity}</span></td>
                  <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${b.remediation_status === 'Open' ? 'bg-status-alert-bg text-status-alert' : b.remediation_status === 'In Progress' ? 'bg-status-watch-bg text-status-watch' : 'bg-status-on-track-bg text-status-on-track'}`}>{b.remediation_status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Hazard Table */}
      <div className="bg-white border border-border-default rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-border-default">
          <h3 className="font-heading font-semibold text-sm text-content-primary">Geospatial Hazard Exposure</h3>
        </div>
        <div className="overflow-x-auto">
          <table data-testid="hazard-table" className="w-full text-[12px]">
            <thead><tr className="bg-surface-muted">
              {['Province', 'Region', 'Hazard Type', 'Severity', 'Exposure (₱Bn)', 'Sector', 'BSFIs Affected'].map(h => (
                <th key={h} className="text-left px-4 py-2.5 font-semibold text-content-secondary text-[10px] uppercase tracking-wider">{h}</th>
              ))}
            </tr></thead>
            <tbody className="divide-y divide-border-subtle">
              {hazards.map((h, i) => (
                <tr key={i} className="hover:bg-surface-muted/50 transition-colors">
                  <td className="px-4 py-2.5 font-medium">{h.province}</td>
                  <td className="px-4 py-2.5">{h.region}</td>
                  <td className="px-4 py-2.5">{h.hazard_type}</td>
                  <td className="px-4 py-2.5"><span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ backgroundColor: `${RISK_COLORS[h.severity_band] || '#D9943B'}15`, color: RISK_COLORS[h.severity_band] || '#D9943B' }}>{h.severity_band}</span></td>
                  <td className="px-4 py-2.5 font-mono font-medium">{h.exposure_value_bn}</td>
                  <td className="px-4 py-2.5 text-[10px]">{h.affected_sector}</td>
                  <td className="px-4 py-2.5 font-mono font-medium">{h.affected_bsfi_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <DrillDownModal isOpen={!!drillKey} onClose={() => setDrillKey(null)} metricKey={drillKey} />
    </div>
  );
}
